import React, { useState, useEffect, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import importAllImg from "../helpers/import-all-imgs";
import { Popover, Drawer } from 'antd';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useIntl } from 'react-intl';


const imgs = importAllImg(require.context('../img/npf', false, /\.(png|jpe?g|svg)$/));


const FullScreenMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  background: ${props => props.isExpanded ? 'rgba(0, 0, 0, 0.1)' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
  transition: background 0.4s ease;
  
  .menu-content {
    background: white;
    width: ${props => props.isExpanded ? '100vw' : '20px'};
    height: ${props => props.isExpanded ? '100vh' : '20px'};
    border-radius: ${props => props.isExpanded ? '0' : '4px'};
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: ${props => props.isExpanded ? '1' : '0'};
    transform: ${props => props.isExpanded ? 'scale(1)' : 'scale(0)'};
    transform-origin: center center;
  }
  
  .menu-icon {
    position: absolute;
    top: 50px;
    left: 80px;
    width: 54px;
    @media (max-width: 991px) {
      width: 28px;
      top: 25px;
      left: 25px
    }
  }
  
  .close-button {
    position: absolute;
    top: 50px;
    right: 80px;
    width: 36px;
    height: 4px;
    cursor: pointer;
    z-index: 1001;
    background: #354402;
    opacity: ${props => props.isExpanded ? '1' : '0'};
    transition: opacity 0.3s ease 0.2s;

    @media (max-width: 991px) {
      width: 19px;
      height: 2px;
      top: 30px;
      right: 25px;
    }
    
    &:hover {
      opacity: 0.7;
    }
  }
  
  .menu-items {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 60px 40px;
    width: 100%;
    max-width: 600px;
    opacity: 0;
    @media (max-width: 991px) {
      padding-top: 0;
      margin-top: -150px;
      gap: 15px;
    }
    transform: translateY(-200px);
    ${props => props.isExpanded ? `
      animation: slideDownContent 0.6s ease 0.3s forwards;
    ` : ''}
  }
  
  @keyframes slideDownContent {
    from {
      opacity: 0;
      transform: translateY(-200px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .section-title {
    font-size: 18px;
    color: #757575;
    font-weight: 400;
    margin-top: 8px;
    text-align: center;
    width: 100%;
    @media (max-width: 991px) {
      font-size: 14px;
    }
    font-family: 'Be Vietnam Pro', sans-serif;
  }
  
  .menu-link {
    font-size: 24px;
    color: #757575;
    cursor: pointer;
    text-align: center;
    font-weight: 500;
    transition: color 0.2s;
    font-family: 'Be Vietnam Pro', sans-serif;

    @media (max-width: 991px) {
      font-size: 18px;
    }
    
    &:hover {
      color: #354402;
    }
    
    &.service-focus {
      font-weight: 600;
      color: #354402;
      font-size: 24px;
      @media (max-width: 991px) {
        font-size: 18px;
      }
    }
  }
`;

function Header({toggleLang, lang}) {

  const navigate = useNavigate();
  const { formatMessage, locale } = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);

  const fm = id => formatMessage({id});

  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Calculate opacity for background and text based on scroll position
  const scrollThreshold = 100;
  const bgOpacity = Math.max(0, Math.min(1, 1 - (scrollY / scrollThreshold)));
  const textOpacity = Math.max(0, Math.min(1, 1 - (scrollY / scrollThreshold)));
  // Move to top-0 when scrolled down (similar to original logic: scrollTop > 50)
  const shouldMoveToTop = scrollY >  50;


  const openMenu = () => {
    setMenuOpen(true);
    // Trigger expansion after a small delay to ensure the element is rendered
    setTimeout(() => {
      setMenuExpanded(true);
    }, 10);
  };

  const closeMenu = () => {
    setMenuExpanded(false);
    // Wait for animation to complete before removing from DOM
    setTimeout(() => {
      setMenuOpen(false);
    }, 400);
  };

  const handleMenuNavigation = (url) => {
    navigate(url);
    closeMenu();
  };
  return (
    <>
      <section
        id='header'
        className={`h-[60px] md:h-[90px] fixed left-0 right-0 z-30 w-full flex justify-between items-center px-[7.5%] font-["Be_Vietnam_Pro",sans-serif] ${
          shouldMoveToTop ? 'top-0' : 'top-[30px] md:top-[60px]'
        }`}
        style={{
          backgroundColor: `rgba(255, 255, 255, ${bgOpacity})`,
          transition: 'background-color 0.5s ease',
        }}
      >
        <div onClick={() => navigate('/')} className='flex items-center gap-5 cursor-pointer'>
          <img className='h-[28px] md:h-[54px]' src={imgs['Group 2898 (2).svg']} alt="Logo" />
          <span
            className='font-bold text-xs md:text-2xl text-[#354402] transition-opacity duration-500'
            style={{ opacity: textOpacity }}
          >
            NGUYỄN PHƯƠNG FOUNDATION
          </span>
        </div>
        <div onClick={openMenu} className='cursor-pointer'>
          <img className='w-[18px] md:w-auto' src={imgs['header-icon.svg']} alt="Menu" />
        </div>
      </section>

      {menuOpen && (
        <FullScreenMenu
          isVisible={menuOpen}
          isExpanded={menuExpanded}
        >
          <div className="menu-content">
            <img className='menu-icon' src={imgs['Group 2898 (2).svg']}/>
            <div className="close-button" onClick={closeMenu}></div>
            <div className="menu-items">
              <div className="section-title">{fm('OUR SERVICE FOCUS AREAS') || 'Our Service Focus Areas'}</div>
              <div className="menu-link service-focus" onClick={() => handleMenuNavigation('/ourservice/innertransformation')}>
                {fm('INNER TRANSFORMATION')}
              </div>
              <div className="menu-link service-focus" onClick={() => handleMenuNavigation('/ourservice/education')}>
                {fm('Education For All')}
              </div>
              <div className="menu-link service-focus" onClick={() => handleMenuNavigation('/ourservice/communities')}>
                {fm('THRIVING COMMUNITIES')}
              </div>
              <div className="menu-link service-focus" onClick={() => handleMenuNavigation('/ourservice/changemakers')}>
                {fm('PLATFORM FOR CHANGEMAKERS')}
              </div>
              <div className="menu-link service-focus" onClick={() => handleMenuNavigation('/ourservice/inspiring-vietnam')}>
                {fm('INSPIRING VIETNAM')}
              </div>

              <div className="menu-link mt-2" onClick={() => handleMenuNavigation('/about')}>
                {fm('ABOUT US')}
              </div>
              <div className="menu-link" onClick={() => handleMenuNavigation('/blog')}>
                {fm('BLOG')}
              </div>
              <div className="menu-link" onClick={() => handleMenuNavigation('/news')}>
                {fm('NEWS')}
              </div>
              <div className="menu-link" onClick={() => handleMenuNavigation('/contact')}>
                {fm('Contact Us')}
              </div>
            </div>
          </div>
        </FullScreenMenu>
      )}
    </>
  );
}


export default Header;
